import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  Banknote,
  CalendarDays,
  LogOut,
  ShieldCheck,
  Users2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { BrandHeader } from "@/components/layouts/BrandHeader";

/**
 * Organizer admin shell — dense layout for power users.
 * All paths under /app/* (consistent with other persona shells).
 */
export function OrganizerShell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user, logout } = useAuth();

  const nav = [
    { to: "/app", label: "Dashboard", icon: Activity, exact: true },
    { to: "/app/tenants", label: "Partners", icon: Users2, exact: false },
    { to: "/app/schedule", label: "Schedule", icon: CalendarDays, exact: false },
    { to: "/app/finance", label: "Finance", icon: Banknote, exact: false },
    { to: "/app/audit", label: "Audit log", icon: ShieldCheck, exact: false },
  ] as const;

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-56 shrink-0 flex-col border-r bg-card md:flex">
        <BrandHeader compact stacked>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-[#e3c071]/80">
            Organizer
          </span>
        </BrandHeader>
        <nav className="flex-1 space-y-0.5 p-2 text-sm">
          {nav.map((item) => {
            const active = item.exact ? path === item.to : path.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to as never}
                className={cn(
                  "flex items-center gap-3 rounded px-3 py-2 transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                <item.icon className="size-4" /> {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t p-3">
          <div className="mb-2 truncate text-xs text-muted-foreground">{user?.email}</div>
          <Button
            onClick={() => void logout()}
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2"
          >
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-6 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
